import { ApplicationRoutes } from "../../components/routes/config";
import * as Config from "../services/configEndPoints";
import responseConverterService from "../services/responseConverterService";
import signOutService from "../services/signOutService";
import DOMPurify from "dompurify";

// Lazy import to avoid circular dependency with rootReducer
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let store: any;
const getStore = () => {
  if (!store) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    store = require("../../store").default;
  }
  return store;
};

export type ApiResponse<T> = {
  message?: string;
  errorMessage?: string[];
  isError?: boolean;
  responseCode: number;
  data?: T;
  count?: number; // used in grid API calls to get the total records for paging
};

const getAuthToken = () => {
  let tokenString: string | null = sessionStorage.getItem("token");
  if (tokenString == null) tokenString = "{}";
  const userToken = JSON.parse(tokenString);
  return userToken?.token ?? "";
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getHttpHeaders = () => {
  let clientSettingsString: string | null = sessionStorage.getItem("ClientApp");
  if (clientSettingsString == null) clientSettingsString = "{}";
  const clientSettings = JSON.parse(clientSettingsString);
  const siteLanguage =
    getStore()
      .getState()
      .app.siteLanguage.find(
        (t: { key: string; value: string }) =>
          t.key === clientSettings.AppName?.toLowerCase()
      )?.value ?? "";

  return {
    "Content-Type": "application/json",
    Client: clientSettings.AppName,
    Authorization: "Bearer " + getAuthToken(),
    "Accept-Language": siteLanguage,
  };
};

export const getFileHttpHeaders = () => {
  let clientSettingsString: string | null = sessionStorage.getItem("ClientApp");
  if (clientSettingsString == null) clientSettingsString = "{}";
  const clientSettings = JSON.parse(clientSettingsString);
  const siteLanguage =
    getStore()
      .getState()
      .app.siteLanguage.find(
        (t: { key: string; value: string }) =>
          t.key === clientSettings.AppName?.toLowerCase()
      )?.value ?? "";

  return {
    Client: clientSettings.AppName,
    Authorization: "Bearer " + getAuthToken(),
    "Accept-Language": siteLanguage,
  };
};

export const get = async <TResponse>(
  endpoint: string
): Promise<ApiResponse<TResponse>> => {
  try {
    let request = await fetch(Config.ApiBaseUrl + endpoint, {
      method: "GET",
      headers: getHttpHeaders(),
    });

    if (request.status === 401) {
      request = await handle401(request, endpoint, "GET");
    }

    return await responseConverterService.convertToApiResponse(request);
  } catch {
    return Promise.resolve({ responseCode: 400 });
  }
};

export const getFilenameFromContentDisposition = (
  contentDisposition: string
): string => {
  // First, try to match the filename from the UTF-8 encoded part
  const filenameRegex = /filename\*=UTF-8''([^;]+)/i;
  let match = filenameRegex.exec(contentDisposition);

  if (match?.[1]) {
    // If found, decode the URI-encoded filename
    return decodeURIComponent(match[1]);
  }

  // If not found, try to match the regular filename
  const fallbackRegex = /filename="?([^";]+)"?/i;
  match = fallbackRegex.exec(contentDisposition);

  if (match?.[1]) {
    return match[1];
  }

  // If no filename found, return a default name
  return "download";
};

export const downloadFile = async <TBody>(
  endpoint: string,
  body: TBody
): Promise<{ blob: Blob; filename: string }> => {
  let request = await fetch(Config.ApiBaseUrl + endpoint, {
    method: "POST",
    headers: getHttpHeaders(),
    body: JSON.stringify(body),
  });
  if (request.status === 401) {
    request = await handle401(request, endpoint, "POST", JSON.stringify(body));
  }

  // Check if the response was successful or not, throw error if not.
  if (!request.ok) {
    throw new Error();
  }

  const blob = await responseConverterService.convertToApiBlobResponse(request);

  // Get filename from custom header
  const contentDisposition = request.headers.get("Content-Disposition");
  let filename = "participants.csv";
  if (contentDisposition) {
    filename = getFilenameFromContentDisposition(contentDisposition);
  }
  return { blob, filename };
};
export const post = async <TBody, TResponse>(
  endpoint: string,
  body: TBody
): Promise<ApiResponse<TResponse>> => {
  try {
    let request = await fetch(Config.ApiBaseUrl + endpoint, {
      method: "POST",
      headers: getHttpHeaders(),
      body: JSON.stringify(body),
      credentials: "include",
    });

    if (request.status === 401) {
      request = await handle401(
        request,
        endpoint,
        "POST",
        JSON.stringify(body)
      );
    }

    return await responseConverterService.convertToApiResponse(request);
  } catch {
    return Promise.resolve({ responseCode: 400 });
  }
};

async function handle401(
  request: Response,
  endpoint: string,
  requestType: "POST" | "GET",
  bodyJSON?: string
) {
  const resp = await refreshToken();
  if (resp.responseCode === 200) {
    sessionStorage.setItem("token", JSON.stringify({ token: resp.data }));
    request = await retryRequest(endpoint, requestType, bodyJSON);
  } else {
    signOutService.signoutRedirectCustom();
  }
  return request;
}

async function retryRequest(
  endpoint: string,
  requestType: "POST" | "GET",
  bodyJSON?: string
) {
  // resend intial request again
  return await fetch(Config.ApiBaseUrl + endpoint, {
    method: requestType,
    headers: getHttpHeaders(),
    body: bodyJSON,
  });
}

async function refreshToken() {
  const resp = await fetch(
    Config.ApiBaseUrl + Config.AccountEndPoints.RefreshToken,
    {
      method: "POST",
      headers: getHttpHeaders(),
      body: JSON.stringify(getAuthToken()),
      credentials: "include",
    }
  );
  return await responseConverterService.convertToApiResponse(resp);
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const development: boolean =
  !process.env.REACT_APP_ENV || process.env.REACT_APP_ENV === "development";

export function isDevelopment(): boolean {
  // console.log(
  //   "process.env.REACT_APP_ENV",
  //   process.env.REACT_APP_ENV,
  //   development,
  //   process.env.NODE_ENV
  // );

  return development;
}

export const getNiceBytes = (value: string): string => {
  const units = ["bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  let l = 0,
    n = parseInt(value, 10) || 0;
  while (n >= 1024 && ++l) {
    n = n / 1024;
  }
  return Math.ceil(n).toFixed(0) + " " + units[l];
};

/**
 * Checks if the user has permission to access a page.
 *
 * @param pagePermissions - An array of permissions required to access the page.
 * @param userPermissions - An array of permissions that the user has.
 * @returns `true` if the user has at least one of the required permissions, otherwise `false`.
 */
export const isUserPermittedToAccess = (
  pagePermissions: string[],
  userPermissions: string[]
): boolean => {
  // If any of the page permissions are found in the user's permissions return true, else return false
  return userPermissions.some((r) => pagePermissions.indexOf(r) >= 0);
};

export const getCompletionUrl = (encryptedConifgData: string): string => {
  // Build query manually — do not use URLSearchParams here. The encrypted payload from
  // the API can contain sequences like "&did=Portal"; legacy parsers relied on those
  // becoming separate query keys when appended unencoded (see Completion bootstrap).
  return (
    ApplicationRoutes.Completion +
    "?r=" +
    window.location.pathname.replace(/\/icportal/i, "") +
    "&data=" +
    encryptedConifgData
  );
};

export const getAutoCompleteValue = (): string => {
  return navigator.userAgent.match(/chrome|chromium|crios/i) // Chrome
    ? "disabled"
    : "off";
};

// Function to handle the action when anchor is clicked
export const handleAnchorAction = (href: string) => {
  // Perform scroll action based on href
  const elementId = href.substring(1);
  const elementToScroll = document.getElementById(elementId);
  if (elementToScroll) {
    // offset 150 px for header as its height is fixed to scroll to specific element.
    const headerOffset = 150;
    const elementPosition = elementToScroll.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.scrollY - headerOffset;
    window.scrollTo({ top: offsetPosition, behavior: "smooth" });
  }
};

export const handleAnchorClick = (event: MouseEvent) => {
  event.preventDefault(); // Prevent default anchor behavior
  const target = event.currentTarget as HTMLAnchorElement;
  const href = target.getAttribute("href");

  // Your custom logic here
  if (href) {
    // Call your custom function
    handleAnchorAction(href);
  }
};

export const scrollToTop = (scrollBehavior?: ScrollBehavior): void => {
  const main =
    document.querySelector<HTMLElement>(".registration-layout-main");
  if (main) {
    /* jsdom and some environments omit Element.prototype.scrollTo */
    if (typeof main.scrollTo === "function") {
      main.scrollTo({ top: 0, left: 0, behavior: scrollBehavior });
    } else {
      main.scrollTop = 0;
      main.scrollLeft = 0;
    }
    return;
  }
  window.scrollTo({ top: 0, left: 0, behavior: scrollBehavior });
};

/**
 * Scrolls an element into view within the registration layout scroll container.
 * On desktop the header is fixed outside the scroll area; padding-top on
 * `.registration-layout-main` reserves space for it. Subtracting that padding
 * keeps the target field visible below the header. On mobile the header is in
 * document flow and padding-top is zero, so no extra offset is applied.
 * Falls back to `scrollIntoView` when the registration layout is not present.
 */
export const scrollToElement = (
  element: HTMLElement,
  scrollBehavior?: ScrollBehavior
): void => {
  const main =
    document.querySelector<HTMLElement>(".registration-layout-main");
  if (main) {
    const scrollOffset =
      parseFloat(window.getComputedStyle(main).paddingTop) || 0;
    const mainRect = main.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    const targetScrollTop =
      elementRect.top - mainRect.top + main.scrollTop - scrollOffset;

    if (typeof main.scrollTo === "function") {
      main.scrollTo({
        top: Math.max(0, targetScrollTop),
        left: 0,
        behavior: scrollBehavior,
      });
    } else {
      main.scrollTop = Math.max(0, targetScrollTop);
    }
    return;
  }

  if (typeof element.scrollIntoView === "function") {
    element.scrollIntoView({
      behavior: scrollBehavior,
      block: "start",
    });
  }
};

/**
 * Sanitizes and decodes a given HTML string by first unescaping HTML entities,
 * then removing any potentially unsafe HTML using DOMPurify, and finally extracting
 * the plain text content.
 *
 * @param html - The HTML string to sanitize and decode.
 * @returns The sanitized and decoded plain text string.
 */
export const GetSanitizeText = (html: string): string => {
  // Step 1: Unescape HTML entities
  const parser = new DOMParser();
  const unescaped =
    parser.parseFromString(html, "text/html").documentElement.textContent ?? ""; // fallback to empty string
  // Step 2: Sanitize the unescaped HTML
  html = DOMPurify.sanitize(unescaped);
  const divContainer = document.createElement("div");
  divContainer.innerHTML = html;
  return divContainer.textContent ?? divContainer.innerText ?? ""; // fallback to empty string
};
